import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { PublicJob } from '@etnrlz-jobs/shared';
import { api, ApiError, uploadFile } from '../lib/api';
import { ErrorBox, Spinner } from '../components/ui';
import { Markdown } from '../components/Markdown';

const RESUME_MAX_MB = 10;
const VIDEO_MAX_MB = 300;

interface UploadState {
  file: File | null;
  progress: number;
  key: string | null;
  uploading: boolean;
  error: string;
}

const emptyUpload: UploadState = { file: null, progress: 0, key: null, uploading: false, error: '' };

export function ApplyPage() {
  const { slug } = useParams<{ slug: string }>();
  const [job, setJob] = useState<PublicJob | null>(null);
  const [loadError, setLoadError] = useState('');
  const [notFound, setNotFound] = useState(false);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [linkedin, setLinkedin] = useState('');
  const [instagram, setInstagram] = useState('');
  const [portfolio, setPortfolio] = useState('');
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [testAnswers, setTestAnswers] = useState<Record<string, string>>({});
  const [resume, setResume] = useState<UploadState>(emptyUpload);
  const [video, setVideo] = useState<UploadState>(emptyUpload);
  const [challenge, setChallenge] = useState<{ question: string; token: string } | null>(null);
  const [challengeAnswer, setChallengeAnswer] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api<{ job: PublicJob }>(`/public/jobs/${slug}`)
      .then((r) => setJob(r.job))
      .catch((e) => {
        if (e instanceof ApiError && e.status === 404) setNotFound(true);
        else setLoadError(e instanceof Error ? e.message : 'Erro ao carregar a vaga');
      });
  }, [slug]);

  const loadChallenge = () => {
    setChallengeAnswer('');
    api<{ question: string; token: string }>('/public/challenge')
      .then(setChallenge)
      .catch(() => setChallenge(null));
  };
  useEffect(loadChallenge, []);

  const workModelLabel = useMemo(() => {
    if (!job) return '';
    return { remoto: 'Remoto', hibrido: 'Híbrido', presencial: 'Presencial' }[job.workModel];
  }, [job]);

  async function doUpload(
    kind: 'resume' | 'video',
    file: File,
    set: (fn: (s: UploadState) => UploadState) => void,
  ) {
    const maxMb = kind === 'resume' ? RESUME_MAX_MB : VIDEO_MAX_MB;
    if (file.size > maxMb * 1024 * 1024) {
      set(() => ({ ...emptyUpload, error: `Arquivo maior que ${maxMb}MB` }));
      return;
    }
    set(() => ({ file, progress: 0, key: null, uploading: true, error: '' }));
    try {
      const presigned = await api<{ key: string; url: string }>(`/public/jobs/${slug}/presign`, {
        method: 'POST',
        body: { kind, contentType: file.type, size: file.size, fileName: file.name },
      });
      await uploadFile(presigned.url, file, (pct) =>
        set((s) => ({ ...s, progress: pct })),
      );
      set((s) => ({ ...s, uploading: false, key: presigned.key }));
    } catch (err) {
      set(() => ({
        ...emptyUpload,
        error: err instanceof Error ? err.message : 'Falha no upload',
      }));
    }
  }

  const canSubmit =
    name.trim().length >= 2 &&
    /\S+@\S+\.\S+/.test(email) &&
    !resume.uploading &&
    !video.uploading &&
    (!job?.requireResume || Boolean(resume.key)) &&
    (!job?.requireVideo || Boolean(video.key)) &&
    Boolean(challenge) &&
    challengeAnswer.trim() !== '' &&
    (job?.questions ?? []).every((q) => !q.required || (answers[q.id] ?? '').trim() !== '') &&
    (job?.tests ?? []).every((t) => (testAnswers[t.id] ?? '').trim() !== '');

  const submit = async () => {
    if (!job) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      await api(`/public/jobs/${slug}/apply`, {
        method: 'POST',
        body: {
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim(),
          linkedin: linkedin.trim(),
          instagram: instagram.trim(),
          portfolio: portfolio.trim(),
          answers: job.questions.map((q) => ({ questionId: q.id, value: answers[q.id] ?? '' })),
          testAnswers: job.tests.map((t) => ({ testId: t.id, value: testAnswers[t.id] ?? '' })),
          resumeKey: resume.key ?? undefined,
          videoKey: video.key ?? undefined,
          challengeToken: challenge?.token ?? '',
          challengeAnswer: challengeAnswer.trim(),
          website: honeypot,
        },
      });
      setDone(true);
      window.scrollTo(0, 0);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Erro ao enviar candidatura');
      // Desafio pode ter expirado ou estar errado: gera um novo.
      loadChallenge();
    } finally {
      setSubmitting(false);
    }
  };

  if (notFound) {
    return (
      <CenterMessage title="Vaga não encontrada" text="Confira se o link está correto." />
    );
  }
  if (loadError) {
    return <CenterMessage title="Algo deu errado" text={loadError} />;
  }
  if (!job) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner className="h-8 w-8 text-brand-400" />
      </div>
    );
  }
  if (job.status === 'closed') {
    return (
      <CenterMessage
        title={job.title}
        text="Esta vaga já foi encerrada e não está mais recebendo candidaturas. Obrigado pelo interesse!"
      />
    );
  }
  if (done) {
    return (
      <CenterMessage
        title="Candidatura enviada! 🎉"
        text={`Recebemos sua candidatura para "${job.title}". Se o seu perfil avançar, entraremos em contato pelo email informado.`}
      />
    );
  }

  return (
    <div className="min-h-screen">
      <header className="border-b border-ink-700 bg-ink-950/70">
        <div className="mx-auto max-w-3xl px-4 py-3 text-sm font-bold tracking-widest text-slate-100">
          ETNRLZ <span className="text-brand-400">JOBS</span>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-10">
        {/* Apresentação da vaga */}
        <div className="mb-3 flex flex-wrap gap-2 text-xs">
          <span className="badge bg-ink-700 text-slate-300">{workModelLabel}</span>
          {job.location && <span className="badge bg-ink-700 text-slate-300">{job.location}</span>}
          {job.employmentType && (
            <span className="badge bg-ink-700 text-slate-300">{job.employmentType}</span>
          )}
          {job.salaryRange && (
            <span className="badge bg-ink-700 text-slate-300">{job.salaryRange}</span>
          )}
        </div>
        <h1 className="text-3xl font-bold text-slate-100">{job.title}</h1>
        {job.summary && <p className="mt-3 text-slate-300">{job.summary}</p>}

        <div className="card mt-8">
          <Markdown text={job.scope} />
          {job.requirements && (
            <>
              <h3 className="mt-5 mb-2 text-base font-semibold text-slate-100">Requisitos</h3>
              <Markdown text={job.requirements} />
            </>
          )}
          {job.niceToHave && (
            <>
              <h3 className="mt-5 mb-2 text-base font-semibold text-slate-100">Diferenciais</h3>
              <Markdown text={job.niceToHave} />
            </>
          )}
        </div>

        {/* Formulário */}
        <h2 className="mt-12 mb-4 text-xl font-bold text-slate-100">Candidatar-se</h2>
        <div className="space-y-6">
          <section className="card space-y-4">
            <h3 className="font-semibold text-slate-100">Seus dados</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className="label">Nome completo *</label>
                <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div>
                <label className="label">Email *</label>
                <input
                  className="input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div>
                <label className="label">WhatsApp / Telefone</label>
                <input className="input" value={phone} onChange={(e) => setPhone(e.target.value)} />
              </div>
              <div>
                <label className="label">LinkedIn</label>
                <input
                  className="input"
                  placeholder="linkedin.com/in/voce"
                  value={linkedin}
                  onChange={(e) => setLinkedin(e.target.value)}
                />
              </div>
              <div>
                <label className="label">Instagram</label>
                <input
                  className="input"
                  placeholder="@seuperfil"
                  value={instagram}
                  onChange={(e) => setInstagram(e.target.value)}
                />
              </div>
              <div>
                <label className="label">Portfólio / GitHub</label>
                <input
                  className="input"
                  value={portfolio}
                  onChange={(e) => setPortfolio(e.target.value)}
                />
              </div>
            </div>
          </section>

          {job.questions.length > 0 && (
            <section className="card space-y-5">
              <h3 className="font-semibold text-slate-100">Perguntas</h3>
              {job.questions.map((q) => (
                <div key={q.id}>
                  <label className="label">
                    {q.label} {q.required && '*'}
                  </label>
                  {q.type === 'text' && (
                    <textarea
                      className="input"
                      rows={3}
                      value={answers[q.id] ?? ''}
                      onChange={(e) => setAnswers((s) => ({ ...s, [q.id]: e.target.value }))}
                    />
                  )}
                  {q.type === 'choice' && (
                    <select
                      className="input"
                      value={answers[q.id] ?? ''}
                      onChange={(e) => setAnswers((s) => ({ ...s, [q.id]: e.target.value }))}
                    >
                      <option value="">Selecione…</option>
                      {(q.options ?? []).map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  )}
                  {q.type === 'boolean' && (
                    <div className="flex gap-4 pt-1 text-sm text-slate-300">
                      {['Sim', 'Não'].map((opt) => (
                        <label key={opt} className="flex items-center gap-1.5">
                          <input
                            type="radio"
                            name={q.id}
                            checked={answers[q.id] === opt}
                            onChange={() => setAnswers((s) => ({ ...s, [q.id]: opt }))}
                          />
                          {opt}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </section>
          )}

          {job.tests.length > 0 && (
            <section className="card space-y-6">
              <div>
                <h3 className="font-semibold text-slate-100">Testes práticos</h3>
                <p className="mt-1 text-xs text-slate-500">
                  Responda com suas próprias palavras. Respostas específicas e com exemplos reais
                  contam muito mais que respostas genéricas.
                </p>
              </div>
              {job.tests.map((t) => (
                <div key={t.id}>
                  <p className="mb-1 text-sm font-semibold text-slate-100">{t.title} *</p>
                  <div className="mb-2 rounded-lg bg-ink-950/60 p-3">
                    <Markdown text={t.instructions} />
                  </div>
                  <textarea
                    className="input"
                    rows={6}
                    placeholder="Sua resposta…"
                    value={testAnswers[t.id] ?? ''}
                    onChange={(e) => setTestAnswers((s) => ({ ...s, [t.id]: e.target.value }))}
                  />
                </div>
              ))}
            </section>
          )}

          <section className="card space-y-5">
            <h3 className="font-semibold text-slate-100">Anexos</h3>
            <UploadField
              label={`Currículo em PDF ${job.requireResume ? '*' : '(opcional)'} — até ${RESUME_MAX_MB}MB`}
              accept="application/pdf"
              state={resume}
              onSelect={(f) => void doUpload('resume', f, setResume)}
            />
            <UploadField
              label={`Vídeo: por que você quer trabalhar comigo? ${job.requireVideo ? '*' : '(opcional)'} — até 3 min, ${VIDEO_MAX_MB}MB (mp4, webm ou mov)`}
              accept="video/mp4,video/webm,video/quicktime"
              state={video}
              onSelect={(f) => void doUpload('video', f, setVideo)}
            />
          </section>

          <section className="card space-y-3">
            <h3 className="font-semibold text-slate-100">Verificação anti-robô</h3>
            {/* Honeypot: invisível para humanos, bots genéricos preenchem. */}
            <input
              type="text"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              autoComplete="off"
              tabIndex={-1}
              aria-hidden="true"
              className="absolute -left-[9999px] h-0 w-0 opacity-0"
              name="website"
            />
            {challenge ? (
              <div className="flex items-center gap-3">
                <label className="text-sm text-slate-300">{challenge.question}</label>
                <input
                  className="input w-24"
                  inputMode="numeric"
                  value={challengeAnswer}
                  onChange={(e) => setChallengeAnswer(e.target.value)}
                />
              </div>
            ) : (
              <button type="button" className="btn-secondary text-xs" onClick={loadChallenge}>
                Carregar verificação
              </button>
            )}
          </section>

          {submitError && <ErrorBox message={submitError} />}
          <button className="btn-primary w-full py-3" disabled={!canSubmit || submitting} onClick={submit}>
            {submitting && <Spinner />} Enviar candidatura
          </button>
          <p className="pb-10 text-center text-xs text-slate-500">
            Seus dados serão usados exclusivamente para este processo seletivo.
          </p>
        </div>
      </main>
    </div>
  );
}

function UploadField({
  label,
  accept,
  state,
  onSelect,
}: {
  label: string;
  accept: string;
  state: UploadState;
  onSelect: (file: File) => void;
}) {
  return (
    <div>
      <label className="label">{label}</label>
      <input
        type="file"
        accept={accept}
        className="block w-full text-sm text-slate-400 file:mr-3 file:rounded-lg file:border-0 file:bg-ink-600 file:px-4 file:py-2 file:text-sm file:font-medium file:text-slate-200 hover:file:bg-ink-500"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onSelect(f);
        }}
      />
      {state.uploading && (
        <div className="mt-2">
          <div className="h-1.5 w-full overflow-hidden rounded bg-ink-700">
            <div
              className="h-full bg-brand-500 transition-all"
              style={{ width: `${state.progress}%` }}
            />
          </div>
          <p className="mt-1 text-xs text-slate-500">Enviando… {state.progress}%</p>
        </div>
      )}
      {state.key && !state.uploading && (
        <p className="mt-1 text-xs text-emerald-400">✓ {state.file?.name} enviado</p>
      )}
      {state.error && <p className="mt-1 text-xs text-red-400">{state.error}</p>}
    </div>
  );
}

function CenterMessage({ title, text }: { title: string; text: string }) {
  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <div className="card max-w-md text-center">
        <h1 className="text-lg font-bold text-slate-100">{title}</h1>
        <p className="mt-2 text-sm text-slate-400">{text}</p>
      </div>
    </div>
  );
}
